/**
 * The dot, and the hook behind it.
 *
 * The part that carries meaning — which colour, and whether do-not-disturb gets
 * its bar — is `presenceDot()` in `@shared/presence`, so the apps that draw a
 * person cannot disagree about it. What is here is the two elements around it
 * and the subscription, because each app draws a differently-shaped chip and a
 * shared component would arrive with a prop for each of them describing where
 * it was.
 *
 * The socket itself is `@shared/presence-client`, started once by `state.tsx`
 * and forwarded to website-main by `worker/presence.ts`.
 */

import { useSyncExternalStore } from 'react';
import type React from 'react';

import { getPresence, getPresenceOverride, subscribePresence } from '@shared/presence-client';
import { presenceDot, type Presence } from '@shared/presence';

/**
 * What to draw, and what was chosen.
 *
 * Both are plain strings, so the snapshot is referentially stable and
 * `useSyncExternalStore` has nothing to loop on.
 *
 * The two are not the same question and the picker needs both: an `available`
 * override decays, so somebody who chose it at nine is drawn as away by eleven
 * — `presence` is the dot, `override` is the tick.
 */
export function usePresence(): { presence: Presence; override: Presence | null } {
  const presence = useSyncExternalStore(subscribePresence, getPresence, () => 'unknown' as const);
  const override = useSyncExternalStore(subscribePresence, getPresenceOverride, () => null);
  return { presence, override };
}

/**
 * A dot in the corner of an avatar.
 *
 * The caller supplies the positioned wrapper — this is `position: absolute` and
 * needs something to be absolute inside. `ring` is the **chip's** background
 * rather than the page's, because the dot cuts itself out of it: the topbar chip
 * sits on `--surface-sunken`, and a dot ringed in the page colour has a visible
 * seam.
 *
 * Renders nothing for `unknown`, which is what a page that has not heard yet
 * shows.
 */
export function PresenceDot({
  presence,
  size = 9,
  ring
}: {
  presence: Presence;
  size?: number;
  ring: string;
}) {
  const style = presenceDot(presence, size, ring);
  if (!style) return null;

  return (
    <span aria-hidden="true" style={style.dot as React.CSSProperties}>
      {style.bar && <span style={style.bar as React.CSSProperties} />}
    </span>
  );
}
